import authService, { TOKEN_TYPES } from "./authService";
import {
  Workspace,
  WorkspaceConfiguration,
} from "@/components/UI/TableToolbar/WorkspaceButton/workspace";

const BASE_API_URL = import.meta.env.VITE_API_URL;

// The "admin" scope has no associated trial, so it is served by a dedicated
// backend route without an id segment (avoids building `admin//workspace/`).
function buildWorkspaceBasePath(scope: string, trialId: string): string {
  return scope === "admin"
    ? `${BASE_API_URL}/data/admin/workspace/`
    : `${BASE_API_URL}/data/${scope}/${trialId}/workspace/`;
}

type ApiError = Error & {
  status?: number;
  body?: string;
};

function createApiError(
  status: number,
  body: string,
  prefix: string,
): ApiError {
  const error = new Error(`${prefix}: ${body}`) as ApiError;
  error.status = status;
  error.body = body;
  return error;
}

export type CreateWorkspacePayload = {
  title: string;
  table_name: string;
  is_vivo_template?: boolean;
  configuration: WorkspaceConfiguration;
  data_section?: string;
};

export type UpdateWorkspacePayload = Partial<{
  title: string;
  configuration: WorkspaceConfiguration;
  is_vivo_template: boolean;
}>;

export const workspaceService = {
  getWorkspaces: async (
    scope: string,
    trialId: string,
    tableName?: string,
    dataSection?: string,
  ): Promise<Workspace[]> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const basePath = buildWorkspaceBasePath(scope, trialId);
    const params = new URLSearchParams();
    if (tableName) params.set("table_name", tableName);
    if (dataSection && dataSection !== "all") {
      params.set("data_section", dataSection);
    }
    const query = params.toString();
    const url = query ? `${basePath}?${query}` : basePath;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw createApiError(
        response.status,
        errorText,
        "Failed to fetch workspaces",
      );
    }

    return response.json();
  },

  createWorkspace: async (
    scope: string,
    trialId: string,
    payload: CreateWorkspacePayload,
  ): Promise<Workspace> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(buildWorkspaceBasePath(scope, trialId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw createApiError(
        response.status,
        errorText,
        "Failed to create workspace",
      );
    }

    return response.json();
  },

  updateWorkspace: async (
    scope: string,
    trialId: string,
    workspaceId: number,
    payload: UpdateWorkspacePayload,
  ): Promise<Workspace> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(
      `${buildWorkspaceBasePath(scope, trialId)}${workspaceId}/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw createApiError(
        response.status,
        errorText,
        "Failed to update workspace",
      );
    }

    return response.json();
  },

  deleteWorkspace: async (
    scope: string,
    trialId: string,
    workspaceId: number,
  ): Promise<void> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(
      `${buildWorkspaceBasePath(scope, trialId)}${workspaceId}/`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw createApiError(
        response.status,
        errorText,
        "Failed to delete workspace",
      );
    }
  },

  duplicateWorkspace: async (
    scope: string,
    trialId: string,
    workspace: Workspace,
    newTitle: string,
  ): Promise<Workspace> => {
    const payload: CreateWorkspacePayload = {
      title: newTitle,
      table_name: workspace.table_name,
      is_vivo_template: false,
      configuration: workspace.configuration,
    };

    return workspaceService.createWorkspace(scope, trialId, payload);
  },
};
